<template>
  <div>
    <div class="d-flex align-items-center justify-content-between flex-wrap mb-2" style="gap: 8px">
      <div>
        <h5 class="mb-1">Promotions</h5>
        <div class="text-sm text-secondary">
          Campaigns the POS applies by itself while a bill is being rung. A cashier's own discount
          is never overwritten.
        </div>
      </div>
      <div class="d-flex flex-wrap" style="gap: 8px">
        <material-button @click="revert()" class="btn btn-sm btn-outline-secondary" :disabled="isSaving">
          Revert
        </material-button>
        <material-button @click="addRow()" class="btn btn-sm btn-outline-secondary" :disabled="isSaving">
          + Add
        </material-button>
        <material-button @click="save()" class="btn btn-sm btn-background" :disabled="isSaving">
          Save
        </material-button>
      </div>
    </div>

    <div class="row">
      <div class="col-lg-4 col-12">
        <div class="promo-panel-box promo-list-box">
          <table class="table table-sm align-items-center mb-0 promo-table">
            <tbody>
              <tr
                v-for="(row, index) in promotions"
                :key="row.__key"
                :class="{ 'promo-row-selected': row.__key === selectedKey }"
                @click="selectRow(row.__key)"
              >
                <td>
                  <div class="d-flex align-items-baseline" style="gap: 6px">
                    <span class="promo-index">{{ index + 1 }}</span>
                    <span class="promo-name" :class="{ 'text-secondary fst-italic': !row.name }">
                      {{ row.name || "(unnamed)" }}
                    </span>
                  </div>
                  <div class="text-xs text-secondary promo-meta">
                    {{ givesLabel(row) }} · {{ triggerLabel(row) }}
                  </div>
                </td>
                <td class="text-center promo-col-on" @click.stop>
                  <material-checkbox
                    v-model="row.enabled"
                    :checked="row.enabled"
                    :name="'enabled-' + row.__key"
                  ></material-checkbox>
                </td>
                <td class="text-center promo-col-delete" @click.stop>
                  <i class="material-icons text-sm cursor-pointer promo-delete" @click="removeRow(row.__key)"
                    >delete</i
                  >
                </td>
              </tr>
              <tr v-if="!promotions.length">
                <td colspan="3" class="text-center text-sm text-secondary py-4">No campaigns yet.</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="text-xs text-secondary mt-2">
          Campaigns are applied in this order and a line is only discounted once — put the campaign
          that should win at the top. Drag isn't supported yet; use + Add / Delete to reorder for now.
        </div>
      </div>

      <template v-if="selected">
        <div class="col-lg-4 col-12 mt-4 mt-lg-0">
          <h6>Settings</h6>
          <div class="promo-panel-box">
            <div class="mb-3">
              <label class="text-sm label-form">Promotion label</label>
              <material-input v-model="selected.name" id="promo_name" placeholder="e.g. Happy Hour Coffee" />
            </div>

            <div class="mb-3">
              <label class="text-sm label-form">Promotion type</label>
              <Multiselect
                :searchable="false"
                v-model="selected.type"
                :options="typeOptions"
                :canClear="false"
                @change="onTypeChange"
              />
            </div>

            <div class="row">
              <div class="col-6 mb-3">
                <label class="text-sm label-form">Trigger by</label>
                <Multiselect
                  :searchable="false"
                  v-model="selected.trigger_by"
                  :options="triggerOptions"
                  :canClear="false"
                />
              </div>
              <div class="col-6 mb-3">
                <label class="text-sm label-form">Promotion on</label>
                <Multiselect
                  :searchable="false"
                  v-model="selected.applies_to"
                  :options="appliesToOptions"
                  :canClear="false"
                />
              </div>
            </div>

            <div class="mb-3" v-if="selected.applies_to !== 'all_items'">
              <label class="text-sm label-form">
                {{ selected.applies_to === "report_group" ? "Report group name(s)" : "Item number(s)" }}
              </label>
              <material-input
                v-model="selected.itemsText"
                id="promo_items"
                placeholder="comma or semicolon separated"
              />
            </div>

            <div class="mb-3" v-if="isBillTotalTrigger">
              <label class="text-sm label-form">Bill total trigger ($)</label>
              <material-input v-model="selected.trigger_value" id="promo_trigger_value" type="number" placeholder="0" />
            </div>

            <div class="row">
              <div class="col-6 mb-1">
                <label class="text-sm label-form">Criteria</label>
                <Multiselect
                  :searchable="false"
                  v-model="selected.criteria"
                  :options="criteriaOptions"
                  :canClear="false"
                />
              </div>
              <div class="col-6 mb-1">
                <label class="text-sm label-form">Criteria — qty</label>
                <material-input v-model="selected.criteria_value" id="promo_criteria_value" type="number" placeholder="0" />
              </div>
            </div>
            <div class="text-xs text-secondary mb-3">
              "Equal or bigger" fires once matching items reach this qty; "exact quantity" discounts
              complete sets only and leaves the odd unit at full price.
            </div>

            <div class="row">
              <div class="col-6 mb-3">
                <label class="text-sm label-form">Promotion on — qty</label>
                <material-input v-model="selected.applies_to_quantity" id="promo_applies_qty" type="number" placeholder="0" />
              </div>
              <div class="col-6 mb-3">
                <label class="text-sm label-form">Promotion value</label>
                <material-input v-model="selected.value" id="promo_value" type="number" placeholder="0" />
              </div>
            </div>
            <div class="text-xs text-secondary mb-3">
              "Promotion on — qty" is how many matching units actually get the promotion, 0 = every
              matching unit.
            </div>

            <div class="mb-3" v-if="selected.type !== 'special_price'">
              <label class="text-sm label-form">Unit</label>
              <Multiselect
                :searchable="false"
                v-model="selected.unit"
                :options="unitOptions"
                :canClear="false"
              />
            </div>

            <div class="row mb-2">
              <div class="col-6 d-flex align-items-center">
                <material-checkbox v-model="selected.members_only" :checked="selected.members_only" name="members_only">
                </material-checkbox>
                <span class="text-sm">Members only</span>
              </div>
              <div class="col-6 d-flex align-items-center">
                <material-checkbox v-model="selected.enabled" :checked="selected.enabled" name="campaign_enabled">
                </material-checkbox>
                <span class="text-sm">Running</span>
              </div>
            </div>

            <div class="mb-1">
              <label class="text-sm label-form">Message</label>
              <material-input v-model="selected.message" id="promo_message" placeholder="optional note for the cashier" />
            </div>
          </div>
        </div>

        <div class="col-lg-4 col-12 mt-4 mt-lg-0">
          <h6>Schedule</h6>
          <div class="promo-panel-box">
            <div class="row">
              <div class="col-6 mb-3">
                <label class="text-sm label-form">Valid from</label>
                <material-input v-model="selected.start_date" id="promo_start_date" type="date" />
              </div>
              <div class="col-6 mb-3">
                <label class="text-sm label-form">Valid to</label>
                <material-input v-model="selected.end_date" id="promo_end_date" type="date" />
              </div>
            </div>
            <div class="text-xs text-secondary mb-3">
              Leave the dates empty to run until the campaign is switched off.
            </div>

            <div class="mb-3">
              <label class="text-sm label-form d-block mb-1">Days</label>
              <div class="d-flex flex-wrap" style="gap: 12px">
                <div class="d-flex align-items-center" v-for="day in weekdayOptions" :key="day.value">
                  <material-checkbox
                    :model-value="selected.days.includes(day.value)"
                    :checked="selected.days.includes(day.value)"
                    :name="'day-' + day.value"
                    @change="toggleDay(day.value, $event.target.checked)"
                  ></material-checkbox>
                  <span class="text-sm">{{ day.label }}</span>
                </div>
              </div>
              <div class="text-xs text-secondary mt-1">Leave every day unchecked to run daily.</div>
            </div>

            <div class="row">
              <div class="col-6 mb-1">
                <label class="text-sm label-form">Start time</label>
                <material-input v-model="selected.start_time" id="promo_start_time" type="time" />
              </div>
              <div class="col-6 mb-1">
                <label class="text-sm label-form">End time</label>
                <material-input v-model="selected.end_time" id="promo_end_time" type="time" />
              </div>
            </div>
            <div class="text-xs text-secondary">
              An end time earlier than the start runs overnight (22:00 → 02:00).
            </div>
          </div>
        </div>
      </template>
    </div>

    <material-loading :active="isSaving" />
  </div>
</template>

<script>
import MaterialButton from "@/components/MaterialButton.vue";
import MaterialInput from "@/components/MaterialInput.vue";
import MaterialCheckbox from "@/components/MaterialCheckbox.vue";
import MaterialLoading from "@/components/MaterialLoading.vue";
import Multiselect from "@vueform/multiselect";
import "@vueform/multiselect/themes/default.css";
import SettingPosService from "../services/settingPos.service";

export default {
  name: "PosPromotionsPanel",
  components: {
    MaterialButton,
    MaterialInput,
    MaterialCheckbox,
    MaterialLoading,
    Multiselect,
  },
  props: {
    branchId: {
      type: [Number, String],
      default: 0,
    },
  },
  data() {
    return {
      promotions: [],
      selectedKey: null,
      keySeq: 0,
      isSaving: false,
      typeOptions: [
        { value: "item_discount", label: "Item discount" },
        { value: "bill_discount", label: "Bill discount" },
        { value: "special_price", label: "Special price" },
      ],
      unitOptions: [
        { value: "percent", label: "% off" },
        { value: "amount", label: "$ off" },
      ],
      appliesToOptions: [
        { value: "all_items", label: "All items" },
        { value: "item_numbers", label: "Item numbers" },
        { value: "report_group", label: "Report group" },
      ],
      triggerOptions: [
        { value: "item_purchase", label: "By item purchase" },
        { value: "bill_amount", label: "By bill amount" },
        { value: "non_discounted_subtotal", label: "By non-discounted subtotal" },
      ],
      criteriaOptions: [
        { value: "equal_or_bigger_qty", label: "Equal or bigger qty" },
        { value: "exact_quantity", label: "Exact quantity (complete sets)" },
        { value: "equal_or_bigger_amount", label: "Equal or bigger amount" },
        { value: "exact_amount", label: "Exact amount" },
      ],
      weekdayOptions: [
        { value: 0, label: "Sun" },
        { value: 1, label: "Mon" },
        { value: 2, label: "Tue" },
        { value: 3, label: "Wed" },
        { value: 4, label: "Thu" },
        { value: 5, label: "Fri" },
        { value: 6, label: "Sat" },
      ],
    };
  },
  computed: {
    selected() {
      return this.promotions.find((row) => row.__key === this.selectedKey) || null;
    },

    isBillTotalTrigger() {
      return (
        this.selected &&
        (this.selected.trigger_by === "bill_amount" || this.selected.trigger_by === "non_discounted_subtotal")
      );
    },
  },
  watch: {
    branchId() {
      this.load();
    },
  },
  created() {
    if (this.branchId) {
      this.load();
    }
  },
  methods: {
    keyed(row) {
      this.keySeq += 1;
      return {
        ...row,
        __key: "promo-" + this.keySeq,
        itemsText: Array.isArray(row.items) ? row.items.join(", ") : "",
        days: Array.isArray(row.days) ? row.days : [],
      };
    },

    load() {
      if (!this.branchId) {
        return;
      }

      this.isSaving = true;

      SettingPosService.getPromotions(this.branchId).then(
        (response) => {
          this.isSaving = false;

          if (response.data.result.isSuccess) {
            this.promotions = (response.data.data || []).map((row) => this.keyed(row));
            this.selectedKey = this.promotions.length ? this.promotions[0].__key : null;
          } else {
            this.$emit("notify", "danger", response.data.result.message);
          }
        },
        (error) => {
          this.isSaving = false;
          this.$emit("notify", "danger", this.errorMessage(error));
        }
      );
    },

    revert() {
      this.load();
    },

    addRow() {
      const row = this.keyed({
        id: null,
        name: "",
        type: "item_discount",
        value: 0,
        unit: "percent",
        applies_to: "all_items",
        items: [],
        applies_to_quantity: 0,
        trigger_by: "item_purchase",
        trigger_value: 0,
        criteria: "equal_or_bigger_qty",
        criteria_value: 0,
        members_only: false,
        start_date: null,
        end_date: null,
        days: [],
        start_time: "00:00",
        end_time: "23:59",
        enabled: true,
        message: "",
      });
      this.promotions.push(row);
      this.selectedKey = row.__key;
    },

    removeRow(key) {
      const index = this.promotions.findIndex((row) => row.__key === key);

      if (index === -1) {
        return;
      }

      this.promotions.splice(index, 1);

      if (this.selectedKey === key) {
        this.selectedKey = this.promotions.length ? this.promotions[0].__key : null;
      }
    },

    selectRow(key) {
      this.selectedKey = key;
    },

    toggleDay(day, checked) {
      if (!this.selected) {
        return;
      }

      const days = this.selected.days.filter((d) => d !== day);

      if (checked) {
        days.push(day);
      }

      this.selected.days = days;
    },

    onTypeChange(value) {
      if (value === "special_price" && this.selected) {
        this.selected.unit = "amount";
      }
    },

    givesLabel(row) {
      const unitLabel = row.unit === "percent" ? "%" : "$";
      const typeLabel = (this.typeOptions.find((t) => t.value === row.type) || {}).label || row.type;

      if (row.type === "special_price") {
        return typeLabel + " $" + (row.value || 0);
      }

      return typeLabel + " " + (row.value || 0) + unitLabel;
    },

    triggerLabel(row) {
      if (row.trigger_by === "bill_amount") {
        return "Bill reaches $" + (row.trigger_value || 0);
      }

      if (row.trigger_by === "non_discounted_subtotal") {
        return "Full-price total reaches $" + (row.trigger_value || 0);
      }

      return row.applies_to === "all_items" ? "Buy any item" : "Buy matching items";
    },

    save() {
      this.isSaving = true;

      const items = this.promotions.map((row) => ({
        id: row.id,
        name: row.name,
        type: row.type,
        value: row.value,
        unit: row.unit,
        applies_to: row.applies_to,
        items: (row.itemsText || "")
          .split(/[,;\n\r]+/)
          .map((v) => v.trim())
          .filter((v) => v !== ""),
        applies_to_quantity: row.applies_to_quantity,
        trigger_by: row.trigger_by,
        trigger_value: row.trigger_value,
        criteria: row.criteria,
        criteria_value: row.criteria_value,
        members_only: row.members_only,
        start_date: row.start_date || null,
        end_date: row.end_date || null,
        days: row.days,
        start_time: row.start_time,
        end_time: row.end_time,
        enabled: row.enabled,
        message: row.message,
      }));

      SettingPosService.savePromotions({ branch_id: this.branchId, items }).then(
        (response) => {
          this.isSaving = false;

          if (response.data.result.isSuccess) {
            const keepSelectedAt = this.promotions.findIndex((row) => row.__key === this.selectedKey);
            this.promotions = (response.data.data || []).map((row) => this.keyed(row));
            this.selectedKey = this.promotions[keepSelectedAt]
              ? this.promotions[keepSelectedAt].__key
              : this.promotions.length
              ? this.promotions[0].__key
              : null;
            this.$emit("notify", "success", "Promotions saved");
          } else {
            this.$emit("notify", "danger", response.data.result.message);
          }
        },
        (error) => {
          this.isSaving = false;
          this.$emit("notify", "danger", this.errorMessage(error));
        }
      );
    },

    errorMessage(error) {
      const result = error && error.response && error.response.data ? error.response.data.result : null;
      return result && result.message ? result.message : String(error);
    },
  },
};
</script>

<style scoped>
.promo-panel-box {
  border: 1px solid #e2e2e2;
  border-radius: 10px;
  padding: 16px;
}

.promo-list-box {
  padding: 0;
  max-height: 420px;
  overflow-y: auto;
}

.promo-table td {
  vertical-align: middle;
  padding-top: 10px;
  padding-bottom: 10px;
}

.promo-table tr {
  cursor: pointer;
}

.promo-table tr:not(:last-child) td {
  border-bottom: 1px solid #f0f2f5;
}

.promo-col-on,
.promo-col-delete {
  width: 1%;
  white-space: nowrap;
}

.promo-index {
  color: #adb5bd;
  font-weight: 600;
  font-size: 0.75rem;
}

.promo-name {
  font-weight: 600;
  color: #344767;
  font-size: 0.875rem;
}

.promo-meta {
  margin-top: 2px;
}

.promo-delete {
  color: #adb5bd;
  transition: color 0.15s;
}

.promo-delete:hover {
  color: #ea0606;
}

.promo-row-selected {
  background-color: #eef2fb;
}

.promo-row-selected .promo-name {
  color: #133067;
}

.multiselect {
  max-width: 100% !important;
  margin-left: 0;
}

.multiselect :deep(.multiselect-single-label) {
  color: #344767;
}
</style>
